/**
 * SectionGuard — garde de route client par section (P2, 2026-07-07).
 *
 * Avant : un deep-link vers une section interdite rendait la page (coquille
 * vide, seuls les appels backend renvoyaient 401/403). Désormais, si
 * l'utilisateur connecté n'a pas la section correspondant à la route (mêmes
 * règles que le filtrage du rail de navigation : hasSection / ROLE_SECTIONS),
 * on rend une page 403 honnête à la place du contenu.
 *
 * Règles de non-régression :
 *  - route non mappée (login "/", /sedec/**, 404) → non gardée ;
 *  - section hors de ALL_SECTIONS (ex. "contenu-diocese", absente de tous les
 *    rôles) → non gardée, sinon même superadmin serait bloqué ;
 *  - pas d'utilisateur en localStorage → non gardée (RouteGuard redirige déjà
 *    les non-authentifiés vers /).
 */
import { useLocation } from "wouter";
import { useAuth, ALL_SECTIONS, SUIVI_DONS_PATH } from "@/lib/auth";
import { sectionForPath } from "@/lib/route-sections";
import Forbidden from "@/pages/forbidden";
import AccesNonConfigure from "@/pages/acces-non-configure";

export default function SectionGuard({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user, hasSection, hasPath } = useAuth();

  // Compte sans aucun rôle reconnu (SEC-6) : écran dédié sur toute route
  // protégée, routes non mappées comprises (sinon une page hors rail, ex.
  // /contenu-diocese, resterait ouverte). « / » reste la page de connexion.
  if (user?.role === "non_configure" && location !== "/") {
    return <AccesNonConfigure />;
  }

  // Suivi des dons (20/10/2026) : l'accès suivi_dons s'ajoute aux autres
  // rôles, quel que soit le profil (un service communication, un caissier ou
  // un auditeur peut l'avoir sans la section Direction générale). La page se
  // décide donc sur le rôle lui-même (pathAllowedForUser), pas sur la section.
  if (user && location.toLowerCase().replace(/\/+$/, "") === SUIVI_DONS_PATH) {
    return hasPath(location) ? <>{children}</> : <Forbidden />;
  }

  const section = sectionForPath(location);
  // Profil en lecture seule (auditeur) : liste blanche stricte. Une route
  // non mappée ou hors univers (contenu-diocese…) lui est refusée aussi.
  if (user?.readOnly && location !== "/" && (section === null || !hasSection(section))) {
    return <Forbidden />;
  }
  const guarded = section !== null && ALL_SECTIONS.includes(section);
  if (user && guarded && !hasSection(section as string)) {
    return <Forbidden />;
  }
  // Rôle à liste blanche de chemins (caissier) : une page hors liste est
  // refusée même si sa section est ouverte, et une route non mappée aussi.
  // Même règle pour les chemins réservés aux administrateurs globaux
  // (espace Paiements, refusé à econome : lib/auth.tsx).
  if (user && location !== "/" && !hasPath(location)) {
    return <Forbidden />;
  }
  return <>{children}</>;
}
